import React from "react";
import { Link } from "react-router-dom";
import { CarIcon, SparklesIcon, CreditCardIcon, PercentIcon, RepeatIcon, BriefcaseIcon, HeadphonesIcon, ArrowRightIcon, MessageCircleIcon, BoxIcon } from "lucide-react";
import { Section, SectionHeading } from "../ui/Section";
import { Reveal } from "../ui/Reveal";
import { whatsappLink } from "../../utils/vehicles";
import { trackPixel } from "../../utils/pixel";
import { CATALOGO_URL } from "../../data/brand";
interface Servicio {
  icon: BoxIcon;
  title: string;
  text: string;
  cta: string;
  /** Ruta interna o, si es WhatsApp, el link externo. */
  to?: string;
  href?: string;
  /** Link externo que no es WhatsApp (ej. catálogo). */
  external?: boolean;
}
const GESTION_WA = 'Hola VCARS, quiero que gestionen la venta de mi auto. ¿Cómo es el proceso?';
const ASESOR_WA = 'Hola VCARS, quiero asesoramiento personalizado para elegir mi próximo auto.';
const servicios: Servicio[] = [{
  icon: CarIcon,
  title: 'Venta de usados seleccionados',
  text: 'Unidades revisadas, con historial y documentación al día.',
  cta: 'Ver catálogo',
  href: CATALOGO_URL,
  external: true
}, {
  icon: SparklesIcon,
  title: 'Venta de vehículos 0KM',
  text: 'Elegí marca y modelo, coordinamos la entrega.',
  cta: 'Ver unidades 0KM',
  href: CATALOGO_URL,
  external: true
}, {
  icon: CreditCardIcon,
  title: 'Financiación hasta el 100%',
  text: 'Simulá tu crédito: evaluamos anticipo, permuta y perfil bancario.',
  cta: 'Simular mi crédito',
  to: '/simulacion-credito'
}, {
  icon: PercentIcon,
  title: 'Tasa 0% en opciones disponibles',
  text: 'Consultá qué unidades tienen tasa promocional.',
  cta: 'Consultar financiación',
  to: '/financiacion'
}, {
  icon: RepeatIcon,
  title: 'Aceptamos permutas',
  text: 'Cargá los datos de tu auto y el monto que pretendés por él.',
  cta: 'Cotizar mi permuta',
  to: '/permutas'
}, {
  icon: BriefcaseIcon,
  title: 'Gestión de ventas particulares',
  text: 'Publicamos, filtramos interesados y cerramos la venta por vos.',
  cta: 'Hablar por WhatsApp',
  href: whatsappLink(GESTION_WA)
}, {
  icon: HeadphonesIcon,
  title: 'Asesoramiento personalizado',
  text: 'Atención clara y cercana en cada paso.',
  cta: 'Hablar por WhatsApp',
  href: whatsappLink(ASESOR_WA)
}];
const cardClass = 'group flex h-full flex-col rounded-2xl border border-line bg-surface2/40 p-6 backdrop-blur-sm transition-[border-color,transform,box-shadow] duration-200 ease-smooth hover:-translate-y-0.5 hover:border-neon/55 hover:shadow-[0_0_32px_-14px_rgba(31,123,255,0.95)] focus-visible:border-neon focus-visible:outline-none';
function CardBody({
  icon: Icon,
  title,
  text,
  cta,
  isWhatsapp
}: Servicio & {
  isWhatsapp: boolean;
}) {
  return <>
      <span className="inline-grid h-11 w-11 place-items-center rounded-xl bg-neon/12 text-neon transition-colors duration-150 ease-smooth group-hover:bg-neon/20" data-id="element-341" data-mp="35despt4f1zpl">
        <Icon className="h-5 w-5" aria-hidden="true" data-id="element-342" data-mp="2vez1zr9awsgm" />
      </span>
      <h3 className="mt-5 text-[17px] font-bold leading-snug text-white" data-id="element-343" data-mp="2vgk01qutqtj8">{title}</h3>
      <p className="mt-2 text-sm leading-relaxed text-mist" data-id="element-344" data-mp="2bv07zvcszdfj">{text}</p>
      <span className={`mt-auto inline-flex items-center gap-1.5 pt-5 text-[13.5px] font-bold ${isWhatsapp ? 'text-[#25c264]' : 'text-neon-soft'}`} data-id="element-345" data-mp="27snbzynuytvy">
        {isWhatsapp ? <MessageCircleIcon className="h-4 w-4" aria-hidden="true" data-id="element-346" data-mp="2grh69idpv86c" /> : null}
        {cta}
        <ArrowRightIcon className="h-4 w-4 transition-transform duration-200 ease-smooth group-hover:translate-x-0.5" aria-hidden="true" data-id="element-347" data-mp="2urjl0vqq9lhd" />
      </span>
    </>;
}
export function Servicios() {
  return <Section id="servicios" data-id="element-348" data-mp="34hl6rgvlyrt4">
      <Reveal data-id="element-349" data-mp="34huroq1sq8ry">
        <SectionHeading title="Nuestros servicios" lead="En VCARS no solo vendemos autos. También acompañamos a quienes quieren vender su vehículo: nos encargamos de la gestión comercial para concretar la operación de manera segura, ordenada y profesional." data-id="element-350" data-mp="34g9vkvzu2hrz" />
      </Reveal>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-id="element-351" data-mp="39hh1epif16w0">
        {servicios.map((servicio, i) => <Reveal key={servicio.title} delay={Math.min(i, 5) * 0.04} className="h-full" data-id="element-352" data-mp="16usivj7ktvrr" data-mpi={i}>
            {servicio.href ? <a href={servicio.href} target="_blank" rel="noopener noreferrer" onClick={servicio.external ? undefined : () => trackPixel('Contact')} className={cardClass} data-id="element-353" data-mp="1bsbz2onu4imc">
                <CardBody {...servicio} isWhatsapp={!servicio.external} data-id="element-354" data-mp="1lindj555aa7h" />
              </a> : <Link to={servicio.to ?? '/'} className={cardClass} data-id="element-355" data-mp="1lkrf3gfkmx4l">
                <CardBody {...servicio} isWhatsapp={false} data-id="element-356" data-mp="nwpmukgm2ykg" />
              </Link>}
          </Reveal>)}
      </div>

      <Reveal data-id="element-357" data-mp="suhs0g69oh6l">
        <p className="mt-8 max-w-4xl text-[15px] leading-relaxed text-mist" data-id="element-358" data-mp="sw2ls3krbw3e">
          También aceptamos permutas, ofrecemos alternativas de financiación de hasta el
          100% y buscamos adaptarnos a los pedidos y posibilidades de cada cliente,
          brindando siempre una atención clara, cercana y personalizada.
        </p>
      </Reveal>
    </Section>;
}